<template>
  <BaseModule
    v-bind="$props"
    class="container promo-multi"
    data-module="promo-multi"
  >
    <div class="content">
      <header
        v-if="title || subtitle || showFilterHotels"
        class="base-title-fontsize"
      >
        <component
          :is="titleTag"
          v-if="title"
          class="tit tit-g"
        >
          {{ title }}
        </component>
        <h3
          v-if="subtitle"
          class="sub"
        >
          {{ subtitle }}
        </h3>

        <div
          v-if="showFilterHotels"
          class="hotels__filter"
        >
          <div
            v-if="metaDestinySelect === 'all'"
            class="filter-item"
          >
            <label
              class="filter-item__label"
              for="field-destino"
              >{{ $t('destination') }}</label
            >
            <v-select
              id="destino"
              v-model="selectedDestiny"
              class="filter-item__field"
              name="field-destino"
              label="Nome"
              :loading="loading"
              :options="destiniesListOptions"
              :clearable="false"
              :reduce="(opt) => opt.Codigo"
            >
              <div slot="no-options">Nenhum resultado encontrado</div>
            </v-select>
          </div>
          <div class="filter-item">
            <label
              class="filter-item__label"
              for="field-hotel"
              >{{ $t('hotel') }}</label
            >

            <v-select
              id="hotel-promo"
              v-model="selectedHotel"
              class="filter-item__field"
              name="field-hotel"
              label="Nome"
              :options="hotelsListOptions"
              :clearable="false"
              :reduce="(opt) => opt.Codigo"
            >
              <div slot="no-options">Nenhum resultado encontrado</div>
            </v-select>
          </div>
        </div>
      </header>

      <EmptyState
        v-if="isEmptyState"
        :title="noPromoTitle"
        :subtitle="noPromoSubtitle"
        :img="promoImg"
      />

      <Loader v-if="loading" />

      <div
        v-for="(group, index) in filteredGroups"
        v-else
        :id="group.Codigo"
        :key="index"
        class="grupo-promocao"
      >
        <div class="group-header">
          <div
            class="bg"
            :style="{ backgroundImage: `url('${getPromoImage(group)}')` }"
            :class="{ 'empty-image': !hasImage(group) }"
          ></div>
          <div class="bloco-tit">
            <h4 class="tit">
              {{ group.Nome }}
            </h4>
            <button
              class="abre-promocao"
              @click="showPromo(group.Codigo)"
            >
              {{
                checkIfDisplayPromo(group.Codigo)
                  ? $t('close')
                  : $t('seeHotels')
              }}
            </button>
          </div>
        </div>
        <div
          v-if="checkIfDisplayPromo(group.Codigo)"
          class="lista-promocoes"
        >
          <div class="wrapper">
            <div
              v-if="hasDescription(group)"
              class="custom-scrollbar"
            >
              <p class="descricaoPrincipal">
                {{ group.Descricao }}
              </p>
            </div>

            <div
              v-for="(promo, idx) in filterPromoList(group.Promocoes)"
              :key="idx"
              class="item-promocao"
            >
              <div class="informacoes">
                <div class="promo-info">
                  <h5 class="nome">{{ promo.Nome }}</h5>

                  <div
                    tabindex="0"
                    class="info-tooltip"
                    @mouseover="showTooltip"
                    @mouseleave="hideTooltip"
                  >
                    <IconHelp size="xs" />
                    <div class="slb-tooltip">
                      <ul class="items-tooltip">
                        <li>{{ promo.ItensDescricao.TipoPagamento }}</li>
                        <li>{{ promo.ItensDescricao.Reembolsavel }}</li>
                        <li>{{ promo.ItensDescricao.Pensao }}</li>
                        <li>{{ promo.ItensDescricao.MinimoNoites }}</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div class="hotel-info">
                  <p class="hotel">
                    {{ promo.Hotel }}
                  </p>
                  <p class="cidade">
                    {{ promo.Cidade }} - {{ promo.CodigoEstado }}
                  </p>
                </div>
              </div>
              <div class="acoes">
                <div class="tarifa">
                  <div class="tarifas">
                    <span class="rotulo-apartir">{{ $t('from') }}</span>
                    <span
                      class="valor c-promo"
                      :style="{ color: getValueColor(promo) }"
                    >
                      {{ getPromoValue(promo) }}
                    </span>
                  </div>
                  <EditorLink
                    v-if="!hasPromoDiscount(promo)"
                    :model="promo.UrlPromocao"
                    class="cta mobile"
                    @click.native="trackCustomEvent(promo)"
                  >
                    {{ $t('seeMore') }}
                  </EditorLink>
                </div>

                <EditorLink
                  v-if="hasPromoDiscount(promo)"
                  :model="promo.UrlPromocao"
                  class="cta"
                  @click.native="trackCustomEvent(promo)"
                >
                  {{ $t('seeMore') }}
                </EditorLink>

                <p
                  v-if="hasPromoDiscount(promo)"
                  class="desconto c-promo"
                >
                  {{ $tc('discount', promo.Tarifa.PercentualDesconto) }}
                </p>

                <p
                  v-if="isLastMinute(promo)"
                  class="last-minute"
                >
                  <span class="rotulo-apartir">
                    {{ $t('from') }} <br />{{ promo.Tarifa.DataInicio }}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </BaseModule>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import vSelect from 'vue-select';

import base from '@/mixins/base';
import Loader from '@/components/Loader.vue';
import IconHelp from '@/components/icons/IconHelp.vue';
import { pushDataLayer } from '@/helpers/gtm';
import handlePromos from '@/helpers/handlePromos';
import randomizeArray from '@/helpers/randomizeArray';
import EmptyState from './EmptyState.vue';
import EditorLink from './EditorLink.vue';
import BaseModule from './generic/BaseModule.vue';

const emptyPromoUrlPath = require('@/assets/promoOptin.png');

export default {
  name: 'PromoMulti',
  components: {
    BaseModule,
    IconHelp,
    Loader,
    EmptyState,
    EditorLink,
    'v-select': vSelect,
  },
  mixins: [base],
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    customNoPromoTitle: {
      type: String,
      default: '',
    },
    customNoPromoSubtitle: {
      type: String,
      default: '',
    },
    hotel: {
      type: Number,
      default: 1,
    },
    showFilter: {
      type: Boolean,
      default: false,
    },
    metaDestinySelect: {
      type: String,
      default: null,
    },
    metaLandingsSelect: {
      type: Number,
      default: null,
    },
    orderBy: {
      type: String,
      default: 'initial-date',
    },
  },
  data() {
    return {
      loading: false,
      promoGroups: [],
      promoExpandList: [],
      destiny: this.metaDestinySelect || 'all',
      hotelCod: -1,
      promoImg: emptyPromoUrlPath,
      isMobile: window.innerWidth < 640,
      destiniesListOptions: [],
    };
  },
  computed: {
    selectedHotel: {
      get() {
        return this.hotelCod;
      },
      set(hotelCod) {
        this.hotelCod = hotelCod;
      },
    },
    selectedDestiny: {
      get() {
        return this.destiny;
      },
      set(destiny) {
        this.destiny = destiny;
        this.hotelCod = -1;
      },
    },
    noPromoTitle() {
      return this.customNoPromoTitle || this.$t('notAvailable');
    },
    noPromoSubtitle() {
      return this.customNoPromoSubtitle || this.$t('notAvailableSubtitle');
    },
    filteredGroups() {
      return this.promoGroups.filter(
        (group) => this.filterPromoList(group.Promocoes).length > 0,
      );
    },
    hasPromo() {
      const promos = this.promoGroups
        .map((group) => this.filterPromoList(group.Promocoes))
        .flat();

      return promos.length > 0;
    },
    showFilterHotels() {
      return this.showFilter || this.metaDestinySelect === undefined;
    },
    enabledPromoGroups() {
      if (this.metaDestinySelect === 'all') {
        return this.promoGroups;
      }

      return this.promoGroups.filter(({ Promocoes }) =>
        Promocoes.some(
          (promo) => promo.CodigoCidade === this.metaDestinySelect,
        ),
      );
    },
    hotelsListOptions() {
      const hotelsOptions = [
        {
          Nome: this.$t('allHotels'),
          Codigo: -1,
        },
      ];

      this.enabledPromoGroups.forEach((promoGroup) => {
        promoGroup.Promocoes.forEach((promo) => {
          if (
            !hotelsOptions.find((item) => item.Codigo === promo.CodigoHotel) &&
            (this.destiny === promo.CodigoCidade || this.destiny === 'all')
          ) {
            hotelsOptions.push({
              Nome: promo.Hotel,
              Codigo: promo.CodigoHotel,
            });
          }
        });
      });

      return hotelsOptions;
    },
    isEmptyState() {
      return !this.hasPromo && !this.loading;
    },
    apiLocale() {
      switch (this.$i18n.locale) {
        case 'en':
          return 'en-us';
        case 'es':
          return 'es-es';
        default:
          return 'pt-br';
      }
    },
    ...mapState(['apiData']),
    ...mapGetters(['settings']),
  },
  watch: {
    hotel(val) {
      if (typeof val === 'number') {
        this.requestPromos();
      }
    },
    metaLandingsSelect() {
      this.destiniesListOptions = [];
      this.$store.commit('SET_ACTIVE_EDITABLE_OBJECT_DATA', {
        path: 'data.metaDestinySelect',
        value: null,
      });
      this.requestPromos();
    },
    metaDestinySelect(newMetaDestinySelect) {
      this.destiny = newMetaDestinySelect;
    },
    promoGroups() {
      // Caso tenha apenas um destino na nova lista, já setamos ele
      const optionValue =
        this.destiniesListOptions.length === 2
          ? this.destiniesListOptions[1].Codigo
          : 'all';

      this.$store.commit('SET_ACTIVE_EDITABLE_OBJECT_DATA', {
        path: 'data.metaDestinySelect',
        value: optionValue,
      });
    },
  },
  async mounted() {
    this.loading = true;
    try {
      await this.requestPromos();
    } catch (err) {
      console.warn(err);
    }
    this.loading = false;

    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth < 640;
    });
  },
  methods: {
    setDestiniesList() {
      const destinies = [
        {
          Codigo: 'all',
          Nome: this.$t('allDestinations'),
        },
      ];

      if (this.promoGroups.length > 0) {
        this.promoGroups.forEach((promoGroup) => {
          promoGroup.Promocoes.forEach((promo) => {
            if (!destinies.find((city) => city.Codigo === promo.CodigoCidade)) {
              destinies.push({
                Codigo: promo.CodigoCidade,
                Nome: promo.Cidade,
              });
            }
          });
        });
      }

      this.destiniesListOptions = destinies;
    },
    orderList(list) {
      const promoList = [...list];

      switch (this.orderBy) {
        case 'initial-date':
          return promoList.sort((promoA, promoB) => {
            const date1 = promoA.PeriodoVigente.Inicio;
            const date2 = promoB.PeriodoVigente.Inicio;

            if (!date1) {
              return 1;
            }
            if (!date2) {
              return -1;
            }

            return new Date(date1) - new Date(date2);
          });
        case 'lowest-price':
          return promoList.sort(
            (promoA, promoB) => promoA.Tarifa.Valor - promoB.Tarifa.Valor,
          );
        case 'highest-price':
          return promoList.sort(
            (promoA, promoB) => promoB.Tarifa.Valor - promoA.Tarifa.Valor,
          );
        case 'highest-discount':
          return promoList.sort((promoA, promoB) => {
            const discount1 = this.hasPromoDiscount(promoA)
              ? promoA.Tarifa.PercentualDesconto
              : 0;
            const discount2 = this.hasPromoDiscount(promoB)
              ? promoB.Tarifa.PercentualDesconto
              : 0;

            return discount2 - discount1;
          });
        case 'random':
          return randomizeArray(promoList);
        default:
          return promoList;
      }
    },
    checkIfDisplayPromo(id) {
      return this.promoExpandList.includes(id);
    },
    showPromo(id) {
      if (this.promoExpandList.length === 0) {
        this.promoExpandList.push(id);
        return true;
      }

      if (!this.promoExpandList.includes(id)) {
        this.promoExpandList.push(id);
        return true;
      }

      this.promoExpandList.splice(this.promoExpandList.indexOf(id), 1);
      return false;
    },
    filterPromoList(promos) {
      const destiny =
        this.metaDestinySelect === 'all'
          ? this.destiny
          : this.metaDestinySelect;
      const filteredPromos = promos.filter((promo) => {
        const matchesDestination =
          destiny !== 'all' ? promo.CodigoCidade === destiny : true;
        const matchesHotel =
          this.hotelCod !== -1 ? promo.CodigoHotel === this.hotelCod : true;

        return matchesDestination && matchesHotel;
      });

      return this.orderList(filteredPromos);
    },
    hasImage(promo) {
      return (
        typeof promo.Imagem?.Url === 'string' && /\S/.test(promo.Imagem?.Url)
      );
    },
    hasDescription(promo) {
      return typeof promo.Descricao === 'string' && /\S/.test(promo.Descricao);
    },
    getPromoImage(promo) {
      if (this.isMobile && promo.ImagemMobile) {
        return promo.ImagemMobile;
      }

      return (
        promo.Imagem?.Url ||
        'https://s3-sa-east-1.amazonaws.com/letslandingpage/assets/images/indisponivel.gif'
      );
    },
    getValueColor(promo) {
      return this.hasPromoDiscount(promo)
        ? this.settings.promotionColor
        : this.settings.primaryColor;
    },
    getPromoValue(promo) {
      return promo.Tarifa.ValorFormatado;
    },
    hasPromoDiscount(promo) {
      const tarifa = promo.Tarifa;
      return (
        tarifa.ExibirDesconto &&
        tarifa.TemDesconto &&
        tarifa.PercentualDesconto !== 0
      );
    },
    isLastMinute(promo) {
      return promo.LastMinute !== null;
    },
    async requestPromos() {
      this.loading = true;

      let { letsbookLandingPageId } = this.settings;

      if (this.metaLandingsSelect !== null) {
        letsbookLandingPageId = this.metaLandingsSelect;
      }
      const platform = this.isMobile ? 'mobile' : 'desktop';
      const url = `${this.settings.letsbookApiId}/promocoesLanding/${letsbookLandingPageId}/${platform}/${this.apiLocale}`;

      if (!this.apiData[url]) {
        try {
          await this.$store.dispatch('loadApiData', url);
        } catch {
          return;
        }
      }

      this.promoGroups = await handlePromos(this.apiData[url], {
        returnGroups: true,
      });
      this.setDestiniesList();

      this.loading = false;
    },
    showTooltip(event) {
      const el = event.currentTarget;
      const slbTooltip = el.querySelector('.slb-tooltip');
      const position = el.getBoundingClientRect();
      if (position.top < slbTooltip.clientHeight + 10) {
        slbTooltip.classList.add('open-down');
      } else {
        slbTooltip.classList.add('open-up');
      }
    },
    hideTooltip(event) {
      const el = event.currentTarget;
      const slbTooltip = el.querySelector('.slb-tooltip');
      slbTooltip.classList.remove('open-up', 'open-down');
    },
    trackCustomEvent(promo) {
      pushDataLayer({
        event: 'select_hotel_promotion',
        hotel_id: String(promo.CodigoHotel),
        hotel_name: promo.Hotel,
        promotion_name: promo.Nome,
        price_starting: promo.Tarifa.Valor,
      });
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'config',
            position: 0,
            label: 'Configurações',
            fields: [
              {
                name: 'metaLandingsSelect',
                type: 'SelectLandingPageId',
                label: 'Defina o agrupador de hotéis',
              },
              {
                name: 'metaDestinySelect',
                type: 'VueSelect',
                label: 'Listar promoções do destino',
                options: {
                  watch: true,
                  attribute: 'destiniesListOptions',
                  handler(destiniesListOptions) {
                    return destiniesListOptions.map((option) => ({
                      label: option.Nome,
                      value: option.Codigo,
                    }));
                  },
                },
                isEnabled({ metaLandingsSelect }) {
                  return metaLandingsSelect;
                },
              },
              {
                name: 'showFilter',
                type: 'Checkbox',
                label: 'Mostrar filtro',
              },
              {
                name: 'orderBy',
                type: 'VueSelect',
                label: 'Ordenar promoções por:',
                options: [
                  {
                    label: 'Data de início',
                    value: 'initial-date',
                  },
                  {
                    label: 'Menor preço',
                    value: 'lowest-price',
                  },
                  {
                    label: 'Maior preço',
                    value: 'highest-price',
                  },
                  {
                    label: 'Maior desconto',
                    value: 'highest-discount',
                  },
                  {
                    label: 'Ordem aleatória',
                    value: 'random',
                  },
                ],
              },
            ],
          },
          {
            name: 'nopromo',
            label: 'Aviso de promoção indisponível',
            helpText:
              'Defina uma mensagem que aparecerá para os seus hóspedes quando não houver nenhuma promoção cadastrada.',
            fields: [
              {
                name: 'customNoPromoTitle',
                type: 'ShortText',
                label: 'Título',
              },
              {
                name: 'customNoPromoSubtitle',
                type: 'LongText',
                label: 'Subtítulo',
              },
            ],
          },
        ],
      },
      {
        name: 'appearance',
        groups: [
          {
            name: 'fonts',
            toRemove: ['contentSizeDesk', 'contentSizeMobile'],
          },
        ],
      },
    ],
  },
  i18n: {
    messages: {
      pt: {
        hotel: 'Hotel',
        allHotels: 'Todos os hotéis',
        destination: 'Destino',
        allDestinations: 'Todos os destinos',
        close: 'Recolher',
        seeHotels: 'Ver mais',
        from: 'A partir de',
        discount: 'Até {count}% OFF',
        seeMore: 'Reservar',
        notAvailable: 'Ops, nossas promoções esgotaram!',
        notAvailableSubtitle:
          'Mas fique atento que em breve teremos novas ofertas para você aproveitar.',
      },
      en: {
        hotel: 'Hotel',
        allHotels: 'All hotels',
        destination: 'Destination',
        allDestinations: 'All destinations',
        close: 'Close',
        seeHotels: 'See more',
        from: 'From',
        discount: 'Up to {count}% OFF',
        seeMore: 'Book',
        notAvailable: 'Ops, our promotions are over!',
        notAvailableSubtitle:
          "Stay tuned that we'll soon have new offers for you.",
      },
      es: {
        hotel: 'Hotel',
        allHotels: 'Todos los hoteles',
        destination: 'Destino',
        allDestinations: 'Todos los destinos',
        close: 'Cerrar',
        seeHotels: 'Ver más',
        from: 'A partir de',
        discount: 'Hasta {count}% OFF',
        seeMore: 'Reserva',
        notAvailable: 'Ops, nuestras promociones están agotadas.',
        notAvailableSubtitle:
          'Estén atentos que pronto tendremos nuevas ofertas para usted.',
      },
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep {
  .v-select {
    width: 100% !important;

    input {
      color: $lb-medium-gray;
    }

    .vs__dropdown-toggle {
      border: none;
      padding: 0;
      background: none;

      .vs__selected-options {
        max-width: calc(100% - 23px);

        input {
          margin: 0;
          padding: 0;
        }
      }

      .vs__selected {
        width: calc(100% - 10px);
        display: block;
        margin: 0;
        padding: 0;
        color: $lb-medium-gray;
        text-align: left;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .vs__dropdown-option {
      color: $lb-medium-gray;
      font-size: $size-normal;
      line-height: 1.2;
      white-space: unset;
      padding: 6px 20px;

      &--highlight {
        color: $lb-white;
      }
    }
  }
}

//inicio css filtro
.hotels {
  &__filter {
    display: flex;
    justify-content: center;
    padding: 10px;
    background: $lb-white;
    box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.07);
    margin-top: 40px;

    @include media-breakpoint-down(md) {
      flex-direction: column;
    }

    .filter-item {
      width: 60%;
      display: inline-flex;
      margin: 0 15px;

      @include media-breakpoint-down(md) {
        width: 100%;
        margin: 10px 0;
      }

      @include media-breakpoint-down(lg) {
        display: block;
        text-align: left;

        .v-select {
          margin-top: 5px;
        }
      }

      &__label {
        font-size: 18px;
        font-style: normal;
        font-weight: $lb-semibold;
        line-height: 21px;
        letter-spacing: -0.01em;
        text-align: center;
        color: #7f7f7f;
        margin: 0 15px 0 0;
        align-items: center;
        display: inline-flex;

        @include media-breakpoint-down(md) {
          width: 95px;
        }
      }

      &__field {
        padding: 15px 20px;
        background: #f6f6f6;
        border: 0;
        font-size: 18px;
        line-height: 21px;
        letter-spacing: -0.01em;
        text-align: center;
        color: #949393;
        align-items: center;
        max-width: calc(100% - 60px);

        @include media-breakpoint-down(lg) {
          max-width: 100%;
        }
      }
    }
  }

  &__list {
    text-align: center;

    &__item {
      box-shadow: 0 3px 4px rgba(0, 0, 0, 0.2);
      position: relative;
      display: inline-block;
      width: calc((100% / 3) - 60px);
      margin-bottom: 40px;

      &::after {
        content: '';
        display: block;
        padding-bottom: 100%;
      }

      @include media-breakpoint-up(xl) {
        margin: 30px;
      }

      @include media-breakpoint-down(xl) {
        width: calc((100% / 3) - 40px);
        margin: 20px;
      }

      @include media-breakpoint-down(lg) {
        width: calc((100% / 2) - 60px);
        margin: 30px;
      }

      @include media-breakpoint-down(md) {
        width: 100%;
        margin: 0 0 40px 0;
      }
    }
  }
}
//fim css filtro

.promo-multi {
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);

  .content {
    max-width: 780px;
  }

  .grupo-promocao {
    background: $lb-white;

    + {
      .grupo-promocao {
        margin-top: 20px;
      }
    }

    .group-header {
      height: 180px;
      position: relative;

      .bg {
        height: 130px;
      }

      .empty-image {
        background-size: auto;
        background-color: $lb-white;
      }
    }

    .bloco-tit {
      background-color: var(--primary-color);
      display: flex;
      justify-content: space-between;
      align-items: center;
      height: 50px;
      padding: 0 70px;
      width: 100%;
    }

    .tit {
      color: $lb-white;
      float: left;
      font-weight: $lb-bold;
      text-align: left;
      text-transform: uppercase;
      margin: 0;
      margin-bottom: 0;
    }

    .abre-promocao {
      background: transparent;
      border: 2px solid $lb-white;
      color: $lb-white;
      float: right;
      font-weight: $lb-bold;
      text-transform: uppercase;
      line-height: 28px;
      width: 122px;
      padding: 0px 10px;
      font-size: $size-small;
      cursor: pointer;
    }
  }

  .lista-promocoes {
    background: $lb-white;
    margin: 0 !important;
    position: relative;
    z-index: 1;

    .wrapper {
      padding: 40px 70px;

      .custom-scrollbar {
        max-height: 90px;
        background: #fafafa;
        border-color: var(--primary-color);
        border-left-width: 3px;
        border-left-style: solid;
        margin-bottom: 30px;
      }
    }

    .descricaoPrincipal {
      font-size: $size-small;
      line-height: 1.5em;
      text-align: left;
      padding: 10px 15px;
    }
  }

  .item-promocao {
    display: flex;
    align-items: center;
    padding: 17px 0;
    position: relative;

    + {
      .item-promocao {
        border-top: 1px solid $lb-light-gray;
      }
    }

    .informacoes {
      flex: 1 1 auto;
      text-align: left;
      width: 55%;

      .nome {
        display: inline-block;
        font-weight: $lb-bold;
        margin-right: 10px;
        text-transform: uppercase;
        font-size: $size-normal;
        margin-bottom: 0.5rem;
        line-height: 1.2;
        margin-top: 0;
        color: $gray-90;
      }

      .info-tooltip {
        cursor: pointer;
        vertical-align: text-bottom;
        display: inline-block;
        position: relative;
        max-height: 20px;

        .slb-tooltip {
          visibility: hidden;
          opacity: 0;
          color: $lb-dark-gray;

          &::after,
          &::before {
            left: 50%;
          }

          .items-tooltip {
            line-height: 1.8em;
            text-align: left;
          }

          &.open-up,
          &.open-down {
            opacity: 1;
            visibility: visible;
          }
        }
      }
    }

    .promo-info {
      line-height: 20px;
      margin-bottom: 10px;
    }

    .hotel-info {
      line-height: 30px;
      font-size: $size-small;

      .cidade {
        color: var(--primary-color);
        border: 1px solid var(--primary-color);
        padding: 5px 10px;
        border-radius: 20px;
        max-width: fit-content;
        white-space: nowrap;
      }

      p {
        display: inline;
        line-height: inherit;
      }

      .hotel {
        font-weight: $lb-bold;
        margin-right: 10px;
        color: $gray-90;
      }
    }

    .acoes {
      flex: 1 1 230px;
      text-align: right;
      width: 40%;

      .cta {
        float: right;
        font-size: $size-small;
        max-height: 30px;
        min-height: 30px;
        line-height: 30px;
        padding: 0 15px;
      }

      .desconto {
        float: right;
        font-size: $size-small;
        height: 30px;
        line-height: 30px;
        padding: 0 15px;
        border-width: 1px;
        border-style: solid;
        font-weight: $lb-bold;
        margin-right: 10px;
        text-transform: uppercase;
      }

      .desconto.c-promo {
        color: var(--promo-color);
        border-color: var(--promo-color);
      }

      .tarifa {
        line-height: 20px;
        margin-bottom: 10px;
        font-size: $size-small;

        .tarifas {
          margin-bottom: 10px;
        }

        .desconto {
          display: none;
          float: none;
          font-size: 3.75vw;
          height: auto;
          line-height: 2.2em;
          margin-left: 0.5em;
          padding: 0 0.6em;
          vertical-align: middle;
        }
      }
    }

    .valor {
      font-weight: $lb-bold;
    }

    .last-minute {
      background: transparent;
      font-size: $size-smaller;
      line-height: 17px;
      padding: 0;
      font-weight: $lb-normal;
      text-transform: inherit;
      text-align: right;
    }

    .tarifa-especial {
      margin: 0;
    }
  }
}

@include media-breakpoint-only(sm) {
  .wrapper {
    padding: 40px 15px !important;
  }
}

@include media-breakpoint-down(sm) {
  .promo-multi {
    overflow: hidden;

    .grupo-promocao {
      margin-top: 9.375vw;

      .bloco-tit {
        height: 50px;
        padding: 0 4.6875vw;
      }

      .tit {
        margin: 0;
        padding: 0;
        width: 59.375vw;
        font-size: $size-small;
      }

      .abre-promocao {
        padding: 3px 5px;
        font-size: $size-smaller;
      }
    }

    .lista-promocoes {
      .wrapper {
        padding: 0;

        .custom-scrollbar {
          margin: 6.25vw 6.25vw 0;
          max-height: 70px;
        }
      }
    }

    .item-promocao {
      flex-wrap: wrap;
      padding: 5vw 6.25vw;

      .informacoes {
        flex: auto;
        text-align: left;
        width: 100%;

        .info-tooltip {
          z-index: 5;

          .info-txt {
            left: calc(50% - 100px);
            width: 200px;
          }
        }
      }

      .acoes {
        flex: auto;
        text-align: left;
        width: 100%;
        margin-top: 1.5625vw;
        text-align: left;

        .rotulo-apartir {
          font-size: 3.75vw;
        }

        .tarifa {
          margin: 10px 0;
          display: flex;
          justify-content: space-between;
          align-items: center;

          .tarifas {
            margin-bottom: 0;
          }

          .cta.mobile {
            margin: 0 !important;
          }
        }

        .cta {
          margin-top: 10px;
          height: auto;
          line-height: 30px;
          padding: 0 25px;
        }

        .desconto {
          width: fit-content;
          margin-top: 10px;
          float: left;
          margin-top: 10px;
        }
      }

      .promo-info {
        margin: 0;
      }

      .hotel-info {
        font-size: 3.75vw;
        line-height: 1.4em;

        p {
          display: block;
        }

        .hotel {
          font-weight: normal;

          &::after {
            display: none;
          }
        }
      }

      .hotel {
        margin-bottom: 10px;
      }

      .last-minute {
        font-size: res(10);
        margin: 0;
        text-align: left;
      }
    }
  }
}

@include media-breakpoint-down(md) {
  .promo-multi {
    padding-top: var(--padding-top-mobile);
    padding-bottom: var(--padding-bottom-mobile);
  }

  .promo-info {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
  }

  .slb-tooltip {
    right: -100%;
    left: inherit;

    &::before,
    &::after {
      left: 89% !important;
    }
  }
}

.cta {
  &:hover {
    text-decoration: none;
  }
}
</style>
